import { SurfaceType } from '../terrain/SurfaceType';
import { isPointInRegion, regionArea, type Region, type Vec2 } from '../utils/geometry';
import type { Hole } from './Hole';

export type CourseSurfaceRegionType =
  'green' | 'bunker' | 'tee' | 'fairway' | 'rough' | 'outOfBounds' | 'default';

export interface CourseSurfaceCandidate {
  surfaceType: SurfaceType;
  holeId: number;
  regionType: Exclude<CourseSurfaceRegionType, 'default'>;
  priority: number;
  area: number;
}

export interface CourseSurfaceSample {
  surfaceType: SurfaceType;
  holeId?: number;
  regionType: CourseSurfaceRegionType;
  candidates: readonly CourseSurfaceCandidate[];
}

const PRIORITY: Record<Exclude<CourseSurfaceRegionType, 'default'>, number> = {
  outOfBounds: 70,
  bunker: 60,
  green: 50,
  tee: 40,
  fairway: 30,
  rough: 20,
};

/**
 * The authoritative, course-wide playable-surface query. Every containing
 * region is considered before precedence is applied, so a neighbouring
 * fairway can never mask a green. Visual splats consume these same Hole
 * regions. Green feathering is zero; consequently no physics expansion is
 * needed and the authored edge remains readable and exact.
 */
export class CourseSurfaceClassifier {
  constructor(private readonly holes: readonly Hole[]) {}

  classify(x: number, z: number, activeHoleNumber?: number): CourseSurfaceSample {
    const point = { x, z };
    const candidates = this.collect(point);
    candidates.sort((a, b) => {
      if (a.priority !== b.priority) return b.priority - a.priority;
      const activeDifference =
        Number(b.holeId === activeHoleNumber) - Number(a.holeId === activeHoleNumber);
      if (activeDifference) return activeDifference;
      if (a.area !== b.area) return a.area - b.area;
      return a.holeId - b.holeId;
    });
    const selected = candidates[0];
    return selected
      ? {
          surfaceType: selected.surfaceType,
          holeId: selected.holeId,
          regionType: selected.regionType,
          candidates,
        }
      : { surfaceType: SurfaceType.Rough, regionType: 'default', candidates };
  }

  private collect(point: Vec2): CourseSurfaceCandidate[] {
    const result: CourseSurfaceCandidate[] = [];
    const add = (
      hole: Hole,
      regionType: CourseSurfaceCandidate['regionType'],
      surfaceType: SurfaceType,
      regions: readonly Region[],
    ) => {
      for (const region of regions)
        if (isPointInRegion(point, region))
          result.push({
            holeId: hole.number,
            regionType,
            surfaceType,
            priority: PRIORITY[regionType],
            area: regionArea(region),
          });
    };
    for (const hole of this.holes) {
      add(hole, 'outOfBounds', SurfaceType.OutOfBounds, hole.def.outOfBounds);
      add(hole, 'bunker', SurfaceType.Bunker, hole.def.bunkers);
      add(hole, 'green', SurfaceType.Green, [hole.def.green]);
      // Tee boxes are authored as points rather than regions. A conservative
      // four-metre playable pad prevents them from swallowing nearby turf.
      for (const tee of hole.def.teeBoxes) {
        const region: Region = { kind: 'circle', center: tee.position, radius: 4 };
        add(hole, 'tee', SurfaceType.Tee, [region]);
      }
      add(hole, 'fairway', SurfaceType.Fairway, [hole.def.fairway]);
      add(hole, 'rough', SurfaceType.Rough, hole.def.rough);
    }
    return result;
  }
}
